/**
 * V6-011 砺锋台 — 数据驱动棋盘（16×11 网格）
 *
 * Canvas 渲染：大棋盘网格 + 1×2 单元格内倒圆角牌面
 *
 * 设计原则：
 *   - 16×11 大棋盘，Canvas 填满右栏宽度，超出视口自然滚动
 *   - 棋子 1 格高 × 2 格宽，置于网格单元格内部（非交叉点上）
 *   - 每行 8 个棋子（每个占 2 列），阵列排布
 *   - 特定节点有"米"字形装饰（横+竖+双对角线）
 *   - 无棋子间连线
 *   - 当前棋子脉动 + 外光晕
 *
 * V4.16 更新：hover/click 交互 + 2×2 地形 + hover 覆盖层
 * V6-011 更新：COLS 12→16, ROWS 18→11，数据驱动棋盘
 * @see design/constraints/design-language.md
 * @date 2026-07-20
 */

const COLS = 14;
const ROWS = 11;
const PADDING = 48;

// V4.1 固定棋子像素尺寸（ph + gap → 推导 cellH，总宽高稳定）
const PIECE_H = 50;   // 棋子固定高度 px
const PIECE_W = 150;  // 棋子宽度 px（同步 CELL_W 1.5x）
const GAP = 5;        // 棋子间距
const CELL_H = PIECE_H + GAP;           // 网格单元格高度 = 55px
const CELL_W = CELL_H * 1.5;            // 网格单元格宽度 = 82.5px（高宽比 1:1.5）

// ── 类型 ──
export interface BoardPiece {
  id: string;
  type: "chess" | "circle" | "hq" | "terrain";  // terrain = 填充物地形（显示 SVG + 地名）
  col: number;   // 起始列（棋子跨 col 和 col+1 两列）
  row: number;   // 所在行
  name: string;
  rank: string;
  rankIdx: number;
  status: "win" | "loss" | "current" | "locked";
  /** 副标题（V4.13 调参：用于副标显示，如 "c004 · 1v1"） */
  subtitle?: string;
  /** terrain 类型专用：地形 SVG 相对路径（不含 /data/tiles/terrain/ 前缀） */
  terrainSvg?: string;
  /** terrain 类型专用：地形中文名 */
  terrainName?: string;
  /** V4.16 terrain 专用：2x2 尺寸标注（默认 1x2） */
  terrainSize?: "1x2" | "2x2";
}

export interface BoardLink {
  from: string;
  to: string;
  kind: "horizontal" | "vertical" | "diagonal";
}

export interface TacticalMapOptions {
  canvas: HTMLCanvasElement;
  pieces: BoardPiece[];
  links: BoardLink[];
  themeColor: string;
  clearedLevels: string[];
  /** V4.16 棋子点击回调（locked/loss 状态棋子被点击时触发） */
  onPieceClick?: (piece: BoardPiece) => void;
}

export class TacticalMap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private pieces: BoardPiece[] = [];
  private themeColor = "#00E4FF";
  private animId = 0;
  private dpr = 1;
  private pulsePhase = 0;
  private cellW = 0;
  private cellH = 0;
  private totalW = 0;
  private totalH = 0;
  private pieceCoords = new Map<string, { x: number; y: number }>();
  private resizeObserver: ResizeObserver | null = null;

  // 米字节点（棋盘上特定交叉点加装饰）
  private starPoints: Array<{ col: number; row: number }> = [];

  // 地形 SVG 图片缓存
  private terrainImages = new Map<string, HTMLImageElement>();

  // 失败骷髅 SVG（V4.15：sk-02-skull-b.svg 单张缓存，红色 KIA 死亡标记）
  private skullImage: HTMLImageElement | null = null;

  // terrain 颜色（暗棕土色系）
  private static readonly TERRAIN_COLOR = "#c9a45a";
  private static readonly TERRAIN_ALT = "#a68543";
  private static readonly TERRAIN_BG = "#1a1408";

  // 状态染色（V7 参数化配色：胜利红金 / 失败黑绿 / 当前青 / 地形棕金）
  private static readonly LOSS_COLOR = "#7fd962";        // 失败绿（V7 新配色，glow 用）
  private static readonly LOCKED_COLOR = "#8A94A0";      // 暗白（未解锁）
  private static readonly UNIFIED_BORDER = "#2a3a4a";    // 统一深灰蓝边框
  private static readonly WIN_BG = "#e1081c";            // 胜利明红底色
  private static readonly WIN_HATCH = "#7a0a14";         // 胜利深红斜线
  private static readonly WIN_TEXT = "#f8b700";           // 胜利琥珀主文字
  private static readonly WIN_DOT = "#f8b700";            // 胜利琥珀角点
  private static readonly WIN_STAR = "#f8b700";           // 胜利琥珀星
  private static readonly WIN_SUB = "#ffd86b";            // 胜利浅琥珀副标
  private static readonly LOSS_BG = "#0a1218";           // 失败深黑蓝底色
  private static readonly LOSS_HATCH = "#7fd962";         // 失败绿斜线
  private static readonly LOSS_DOT = "#7fd962";           // 失败绿角点
  private static readonly LOSS_SUB = "#7fd962";           // 失败绿副标
  private static readonly LOSS_TEXT = "#e0e6f0";         // 失败浅灰蓝主文字
  private static readonly CUR_BG = "#0a1218";            // 当前深蓝底色
  private static readonly CUR_HATCH = "#00e4ff";          // 当前青斜线
  private static readonly CUR_DOT = "#00e4ff";            // 当前青角点
  private static readonly CUR_GLOW = "#00e4ff";           // 当前青光晕色
  private static readonly CUR_GLOW_ALPHA = "10";          // 当前光晕透明度 ~10%
  private static readonly CUR_GLOW_PAD = 4;               // 当前光晕扩展 px
  private static readonly CUR_TEXT = "#e0e6f0";          // 当前浅灰蓝主文字
  // hat_7fill 参数（V7 参数化）
  private static readonly HATCH_SPACING = 4.5;
  private static readonly HATCH_WIDTH = 1;
  private static readonly HATCH_ANGLE_DEG = 51;           // 斜线角度（度）
  private static readonly HATCH_ALPHA = "2D";             // 非当前牌斜线透明 ~18%
  private static readonly HATCH_ALPHA_CUR = "5C";         // 当前牌斜线透明 ~37%
  // 角点参数
  private static readonly DOT_R = 1.6;                   // 角点半径
  private static readonly DOT_OFF = 0.9;                   // 角点距边偏移
  // 文字描边（V7 参数化）
  private static readonly TEXT_STROKE = 0.8;
  private static readonly TEXT_STROKE_COLOR = "#0a1218";

  // V4.16 hover/click 交互 + 动效
  private hoveredPieceId: string | null = null;
  private lastHoveredPieceId: string | null = null;
  private hoverEnterTime = 0;        // 当前 hover 进入时间（performance.now）
  private hoverLeaveTime = 0;        // 上次 hover 离开时间
  private static readonly HOVER_ANIM_MS = 180;  // hover 动效时长（ms）
  private onPieceClick?: (piece: BoardPiece) => void;

  constructor(opts: TacticalMapOptions) {
    this.canvas = opts.canvas;
    this.ctx = opts.canvas.getContext("2d")!;
    this.pieces = opts.pieces;
    this.themeColor = opts.themeColor;

    // V4.16 点击回调
    if (opts.onPieceClick) {
      this.onPieceClick = opts.onPieceClick;
    }

    this.generateStarPoints();
    this.resize();
    this.observeResize();
    this.startLoop();
    // 地形 SVG 异步预加载（不阻塞首帧）
    this.preloadTerrainImages(opts.pieces);
    // 失败骷髅 SVG 预加载
    this.loadSkullImage();
    // V4.16 鼠标事件绑定
    this.bindMouseEvents();
  }

  // ── 米字节点（每 3 列/行的交叉点）──
  private generateStarPoints(): void {
    this.starPoints = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (c % 3 === 0 && r % 3 === 0) {
          this.starPoints.push({ col: c, row: r });
        }
      }
    }
  }

  // ── 尺寸 & 坐标计算（V4.1 固定棋子像素尺寸）──
  private resize(): void {
    this.dpr = window.devicePixelRatio || 1;

    this.cellW = CELL_W;
    this.cellH = CELL_H;
    this.totalW = PADDING * 2 + COLS * CELL_W;       // +1 格 容纳最右侧棋子右半部分
    this.totalH = PADDING * 2 + ROWS * CELL_H;       // +1 格 容纳最底行棋子下半部分

    this.canvas.width = this.totalW * this.dpr;
    this.canvas.height = this.totalH * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.canvas.style.width = `${this.totalW}px`;
    this.canvas.style.height = `${this.totalH}px`;

    // 缓存牌面坐标
    this.pieceCoords.clear();
    for (const piece of this.pieces) {
      if (piece.type === "chess" || piece.type === "terrain") {
        // 棋子/地形占据 col〜col+2 两列 × row〜row+1 一行的网格空间，居中于单元格内部
        const cx = PADDING + (piece.col + 1) * this.cellW;
        // V4.16 2×2 地形跨度 2 行，中心偏移到 row+1
        const is2x2 = piece.type === "terrain" && piece.terrainSize === "2x2";
        const cy = PADDING + (piece.row + (is2x2 ? 1 : 0.5)) * this.cellH;
        this.pieceCoords.set(piece.id, { x: cx, y: cy });
      } else {
        this.pieceCoords.set(piece.id, this.nodePos(piece.col, piece.row));
      }
    }
  }

  // ── 监听容器宽度变化 ──
  private observeResize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this.resizeObserver.observe(parent);
  }

  // ── V4.16 鼠标事件绑定 ──
  private boundMouseMove?: (e: MouseEvent) => void;
  private boundClick?: (e: MouseEvent) => void;
  private bindMouseEvents(): void {
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundClick = this.handleClick.bind(this);
    this.canvas.addEventListener("mousemove", this.boundMouseMove);
    this.canvas.addEventListener("click", this.boundClick);
  }

  private handleMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    // 校正 CSS 缩放：canvas 逻辑尺寸 vs 实际渲染尺寸的比例
    const scaleX = rect.width > 0 ? this.totalW / rect.width : 1;
    const scaleY = rect.height > 0 ? this.totalH / rect.height : 1;
    const viewport = this.canvas.closest(".s10-viewport") as HTMLElement | null;
    const sx = viewport?.scrollLeft ?? 0;
    const sy = viewport?.scrollTop ?? 0;
    // e.client - rect 是 CSS 像素，乘 scale 转 canvas 逻辑坐标；scroll 已在 canvas 逻辑空间
    const mx = (e.clientX - rect.left) * scaleX + sx;
    const my = (e.clientY - rect.top) * scaleY + sy;

    let newId: string | null = null;
    for (const piece of this.pieces) {
      if (piece.type === "circle" || piece.type === "hq") continue;
      const coord = this.pieceCoords.get(piece.id);
      if (!coord) continue;
      const is2x2 = piece.type === "terrain" && piece.terrainSize === "2x2";
      const ph = is2x2 ? PIECE_H * 2 + GAP : PIECE_H;
      const pw = PIECE_W;
      if (mx >= coord.x - pw / 2 && mx <= coord.x + pw / 2 &&
          my >= coord.y - ph / 2 && my <= coord.y + ph / 2) {
        newId = piece.id;
        break;
      }
    }

    if (newId !== this.hoveredPieceId) {
      // 记录离开时间用于 fade-out 动效
      if (this.hoveredPieceId) {
        this.lastHoveredPieceId = this.hoveredPieceId;
        this.hoverLeaveTime = performance.now();
      }
      this.hoveredPieceId = newId;
      if (newId) this.hoverEnterTime = performance.now();
      const piece = newId ? this.pieces.find(p => p.id === newId) : null;
      const clickable = piece && (piece.status === "locked" || piece.status === "loss");
      this.canvas.style.cursor = clickable ? "pointer" : "default";
    }
  }

  private handleClick(_e: MouseEvent): void {
    if (!this.hoveredPieceId || !this.onPieceClick) return;
    const piece = this.pieces.find(p => p.id === this.hoveredPieceId);
    if (!piece) return;
    // 所有棋子（含 current/locked/loss/win）均可点击，仅地形不触发
    if (piece.type === "chess") {
      this.onPieceClick(piece);
    }
  }

  // ── 节点像素坐标 ──
  private nodePos(col: number, row: number): { x: number; y: number } {
    return {
      x: PADDING + col * this.cellW,
      y: PADDING + row * this.cellH,
    };
  }

  // ── 网格线坐标 ──
  private gridX(col: number): number {
    return PADDING + col * this.cellW;
  }
  private gridY(row: number): number {
    return PADDING + row * this.cellH;
  }

  // ── 动画循环 ──
  private startLoop(): void {
    const loop = () => {
      this.pulsePhase += 0.04;
      this.animId = requestAnimationFrame(loop);
      this.render();
    };
    this.animId = requestAnimationFrame(loop);
  }

  // ── 全文渲染 ──
  private render(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.totalW, this.totalH);

    this.drawBackground(ctx);
    this.drawBoardGrid(ctx);
    this.drawStarPoints(ctx);
    this.drawCellGlows(ctx);
    this.drawPieces(ctx);
    this.drawScanlines(ctx);
    this.drawCoordLabels(ctx);
  }

  // ── 背景 ──
  private drawBackground(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#0F1923";
    ctx.fillRect(0, 0, this.totalW, this.totalH);

    // 4 角 45° 对角线
    ctx.strokeStyle = "rgba(0, 228, 255, 0.04)";
    ctx.lineWidth = 1;
    const step = 24;
    for (let i = 0; i < 10; i++) {
      const off = i * step;
      ctx.beginPath(); ctx.moveTo(off, 0); ctx.lineTo(0, off); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(this.totalW, off); ctx.lineTo(this.totalW - off, 0); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(off, this.totalH); ctx.lineTo(0, this.totalH - off); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(this.totalW - off, this.totalH); ctx.lineTo(this.totalW, this.totalH - off); ctx.stroke();
    }
  }

  // ── 棋盘网格（横线 + 竖线）──
  private drawBoardGrid(ctx: CanvasRenderingContext2D): void {
    // 细线（所有网格）
    ctx.strokeStyle = "rgba(0, 228, 255, 0.07)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let r = 0; r < ROWS; r++) {
      const y = this.gridY(r);
      ctx.moveTo(PADDING, y);
      ctx.lineTo(this.totalW - PADDING, y);
    }
    for (let c = 0; c < COLS; c++) {
      const x = this.gridX(c);
      ctx.moveTo(x, PADDING);
      ctx.lineTo(x, this.totalH - PADDING);
    }
    ctx.stroke();

    // 粗线（每 3 格）
    ctx.strokeStyle = "rgba(0, 228, 255, 0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let r = 0; r < ROWS; r += 3) {
      const y = this.gridY(r);
      ctx.moveTo(PADDING, y);
      ctx.lineTo(this.totalW - PADDING, y);
    }
    for (let c = 0; c < COLS; c += 3) {
      const x = this.gridX(c);
      ctx.moveTo(x, PADDING);
      ctx.lineTo(x, this.totalH - PADDING);
    }
    ctx.stroke();
  }

  // ── 米字形节点装饰 ──
  private drawStarPoints(ctx: CanvasRenderingContext2D): void {
    const hs = this.cellH * 0.08;

    for (const sp of this.starPoints) {
      const { x, y } = this.nodePos(sp.col, sp.row);

      // 有棋子/地形的单元格 4 角节点跳过（米字会干扰牌子台面）
      const hasPiece = this.pieces.some(
        (p) => (p.type === "chess" || p.type === "terrain") &&
          (sp.col === p.col || sp.col === p.col + 2) &&
          (sp.row === p.row || sp.row === p.row + 1)
      );
      if (hasPiece) continue;

      ctx.strokeStyle = "rgba(0, 228, 255, 0.18)";
      ctx.lineWidth = 0.5;

      ctx.beginPath(); ctx.moveTo(x - hs, y); ctx.lineTo(x + hs, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - hs); ctx.lineTo(x, y + hs); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x - hs, y - hs); ctx.lineTo(x + hs, y + hs); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x + hs, y - hs); ctx.lineTo(x - hs, y + hs); ctx.stroke();

      ctx.fillStyle = "rgba(0, 228, 255, 0.35)";
      ctx.beginPath(); ctx.arc(x, y, 1.2, 0, Math.PI * 2); ctx.fill();
    }
  }

  // ── 格子底色光晕（牌面后方，按状态着色）──
  private drawCellGlows(ctx: CanvasRenderingContext2D): void {
    for (const piece of this.pieces) {
      if (piece.type !== "chess" && piece.type !== "terrain") continue;
      const c = this.pieceCoords.get(piece.id);
      if (!c) continue;

      const pw = PIECE_W;
      // V4.16 2×2 地形高度加倍
      const is2x2 = piece.type === "terrain" && piece.terrainSize === "2x2";
      const ph = is2x2 ? PIECE_H * 2 + GAP : PIECE_H;
      const cr = ph * 0.12;
      const x = c.x - pw / 2;
      const y = c.y - ph / 2;

      let glowColor: string;
      switch (piece.status) {
        case "loss":
          glowColor = TacticalMap.LOSS_COLOR + "10";      // 绿微亮 ~10%
          break;
        case "current":
          glowColor = TacticalMap.CUR_GLOW + "10";        // 青微亮 ~10%
          break;
        case "locked":
          if (piece.type === "terrain") {
            glowColor = TacticalMap.TERRAIN_COLOR + "08"; // 地形棕微亮
          } else {
            glowColor = TacticalMap.LOCKED_COLOR + "06";  // 暗白微亮
          }
          break;
        case "win":
          glowColor = TacticalMap.WIN_BG + "0A";          // 红微亮 ~5%
          break;
        default:
          glowColor = TacticalMap.LOCKED_COLOR + "05";
      }

      ctx.fillStyle = glowColor;
      ctx.beginPath();
      this.roundRect(ctx, x, y, pw, ph, cr);
      ctx.fill();
    }
  }

  // ── 胜利红星（左对齐 + 上下居中 + 1.5x 放大，主题红 #FF4655）──
  private drawVictoryFlag(
    ctx: CanvasRenderingContext2D,
    _c: { x: number; y: number },
    _cs: number, pw: number, ph: number,
    x: number, y: number
  ): void {
    const starR = ph * 0.27;              // 1.5x 放大
    const starX = x + pw * 0.06 + starR;  // 左对齐
    const starY = y + ph * 0.5;           // 上下居中
    this.drawStar(ctx, starX, starY, starR, TacticalMap.WIN_STAR);
  }

  // ── 失败骷髅头（V4.15：替换为 sk-02-skull-b.svg，红色 KIA 死亡标记）──
  private drawSkull(
    ctx: CanvasRenderingContext2D,
    _c: { x: number; y: number },
    pw: number, ph: number,
    x: number, y: number
  ): void {
    if (this.skullImage && this.skullImage.complete && this.skullImage.naturalWidth > 0) {
      const iconH = ph * 0.675;           // 1.5x 放大（与地形图标的 drawImage 逻辑一致）
      const iconW = iconH;
      const iconX = x + pw * 0.06;        // 左对齐
      const iconY = y + (ph - iconH) / 2; // 上下居中（drawImage top-left anchor）
      ctx.drawImage(this.skullImage, iconX, iconY, iconW, iconH);
      return;
    }
    // 兜底：图像未加载完时画一个绿色圆，提示"占位"
    const h = ph * 0.42;
    const hw = h * 0.72;
    const sX = x + pw * 0.06 + hw;
    const sY = y + ph * 0.5;
    ctx.fillStyle = "#7fd962";
    ctx.beginPath();
    ctx.arc(sX, sY, hw, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── 预加载失败骷髅 SVG ──
  private loadSkullImage(): void {
    if (this.skullImage) return;
    const relPath = "sk-02-skull-b.svg";
    fetch(`/data/tiles/annotations/${relPath}`)
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.text();
      })
      .then((svgText) => {
        // 替换 CSS 变量为失败绿（V7 LOSS 配色 token）
        const replaced = svgText.replace(/var\(--valorant-red\b[^)]*\)/g, "#7fd962");
        const blob = new Blob([replaced], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          this.skullImage = img;
          URL.revokeObjectURL(url);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          console.warn("[tactical-map] skull SVG load failed:", relPath);
        };
        img.src = url;
      })
      .catch((err) => {
        console.warn("[tactical-map] skull fetch failed:", err);
      });
  }

  // ── 棋子文字（V4.6：暗色描边 + 亮色填充，避开状态色同色对比）──
  private drawPieceText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number, y: number,
    align: CanvasTextAlign, baseline: CanvasTextBaseline,
    fill: string, stroke: string, strokeWidth: number
  ): void {
    ctx.textAlign = align;
    ctx.textBaseline = baseline;
    if (strokeWidth > 0) {
      ctx.lineJoin = "miter";
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth;
      ctx.strokeText(text, x, y);
    }
    ctx.fillStyle = fill;
    ctx.fillText(text, x, y);
  }

  // ── V4.16 hover 动效透明度计算 ──
  /** 返回当前 hover 的动画进度 0~1（ease-out 渐变） */
  private hoverAlpha(): number {
    const now = performance.now();
    const dt = now - this.hoverEnterTime;
    if (dt <= 0) return 0;
    const t = Math.min(dt / TacticalMap.HOVER_ANIM_MS, 1);
    return 1 - (1 - t) ** 2;  // ease-out quad
  }

  /** 返回上次 hover 的淡出进度（1→0），0 表示已完成淡出 */
  private hoverFadeOutAlpha(): number {
    const now = performance.now();
    const dt = now - this.hoverLeaveTime;
    if (dt <= 0) return 1;
    const t = Math.min(dt / (TacticalMap.HOVER_ANIM_MS * 0.6), 1);
    return 1 - (1 - (1 - t) ** 2);  // reversed ease-out
  }

  // ── V4.16 hover 按钮（Canvas 模拟按钮控件）──
  private drawHoverButton(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number,
    btnW: number, btnH: number,
    text: string, color: string
  ): void {
    const btnX = cx - btnW / 2;
    const btnY = cy - btnH / 2;
    const br = 2;

    // 按钮背景
    ctx.fillStyle = color + "25";
    ctx.beginPath();
    this.roundRect(ctx, btnX, btnY, btnW, btnH, br);
    ctx.fill();

    // 按钮边框
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    this.roundRect(ctx, btnX, btnY, btnW, btnH, br);
    ctx.stroke();

    // 按钮文字
    const fSize = Math.max(11, btnH * 0.42);
    ctx.font = `bold ${fSize}px var(--font-serif, serif)`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "miter";
    ctx.strokeStyle = TacticalMap.TEXT_STROKE_COLOR;
    ctx.lineWidth = TacticalMap.TEXT_STROKE;
    ctx.strokeText(text, cx, cy);
    ctx.fillStyle = color;
    ctx.fillText(text, cx, cy);
  }

  // ── 五角星绘制 ──
  private drawStar(
    ctx: CanvasRenderingContext2D,
    cx: number, cy: number, r: number,
    color: string
  ): void {
    const points = 5;
    const innerR = r * 0.38;
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? r : innerR;
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }

  // ── 牌面渲染（全体）──
  private drawPieces(ctx: CanvasRenderingContext2D): void {
    for (const piece of this.pieces) {
      switch (piece.type) {
        case "chess":   this.drawChessPiece(ctx, piece); break;
        case "circle":  this.drawCirclePiece(ctx, piece); break;
        case "hq":      this.drawHQPiece(ctx, piece); break;
        case "terrain": this.drawTerrainPiece(ctx, piece); break;
      }
    }
  }

  // ── 倒圆角棋子（1 格高 × 2 格宽，统一暗灰边框 + 斜线填充）──
  private drawChessPiece(ctx: CanvasRenderingContext2D, piece: BoardPiece): void {
    const c = this.pieceCoords.get(piece.id);
    if (!c) return;

    const cs = this.cellH;
    const pw = PIECE_W;
    const ph = PIECE_H;
    const cr = ph * 0.16;

    const isWin = piece.status === "win";
    const isLoss = piece.status === "loss";
    const isCurrent = piece.status === "current";

    const x = c.x - pw / 2;
    const y = c.y - ph / 2;

    // ── 按状态选配色 ──
    let bg: string, hatch: string, dot: string;
    if (isWin) {
      bg = TacticalMap.WIN_BG;    hatch = TacticalMap.WIN_HATCH;   dot = TacticalMap.WIN_DOT;
    } else if (isLoss) {
      bg = TacticalMap.LOSS_BG;   hatch = TacticalMap.LOSS_HATCH;  dot = TacticalMap.LOSS_DOT;
    } else {
      bg = TacticalMap.CUR_BG;    hatch = TacticalMap.CUR_HATCH;   dot = TacticalMap.CUR_DOT;
    }

    // 外光晕（仅 current，单层 3px 6%）
    if (isCurrent) {
      ctx.fillStyle = TacticalMap.CUR_GLOW + TacticalMap.CUR_GLOW_ALPHA;
      ctx.beginPath();
      this.roundRect(ctx, x - TacticalMap.CUR_GLOW_PAD, y - TacticalMap.CUR_GLOW_PAD,
        pw + TacticalMap.CUR_GLOW_PAD * 2, ph + TacticalMap.CUR_GLOW_PAD * 2, cr + 2);
      ctx.fill();
    }

    // 填充底色
    ctx.fillStyle = bg;
    ctx.beginPath();
    this.roundRect(ctx, x, y, pw, ph, cr);
    ctx.fill();

    // 斜线填充
    this.drawHatchFill(ctx, x, y, pw, ph, cr, hatch, isCurrent);

    // 统一外框（统一 1px）
    ctx.strokeStyle = TacticalMap.UNIFIED_BORDER;
    ctx.lineWidth = 1;
    ctx.beginPath();
    this.roundRect(ctx, x, y, pw, ph, cr);
    ctx.stroke();

    // 四角状态圆点
    ctx.fillStyle = dot;
    for (const [dx, dy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
      ctx.beginPath();
      const dotX = dx > 0 ? x + TacticalMap.DOT_OFF + TacticalMap.DOT_R
                           : x + pw - TacticalMap.DOT_OFF - TacticalMap.DOT_R;
      const dotY = dy > 0 ? y + TacticalMap.DOT_OFF + TacticalMap.DOT_R
                           : y + ph - TacticalMap.DOT_OFF - TacticalMap.DOT_R;
      ctx.arc(dotX, dotY, TacticalMap.DOT_R, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── 文字（V4.14：主文字 rank + 罗马位号 + 副标 c00X · 1v1，0.5px 描边回归）──
    const stk = TacticalMap.TEXT_STROKE;
    const stkColor = TacticalMap.TEXT_STROKE_COLOR;
    const starR = ph * 0.27;          // win/loss icon 半径
    const hasIcon = isWin || isLoss;
    const textAreaX = hasIcon ? x + pw * 0.06 + starR * 2 + 6 : x;
    const textAreaW = pw - (textAreaX - x);
    const textCenterX = textAreaX + textAreaW / 2;

    // 主文字：rank + 罗马位号
    const position = Math.floor(piece.col / 2) + 1;
    const roman = ["I", "II", "III", "IV", "V", "VI"][position - 1] ?? String(position);
    const mainText = `${piece.rank} ${roman}`;
    const fSize = 17;
    ctx.font = `bold ${fSize}px var(--font-serif, serif)`;

    if (isWin) {
      this.drawVictoryFlag(ctx, c, cs, pw, ph, x, y);
      this.drawPieceText(ctx, mainText, textCenterX, y + ph * 0.42, "center", "middle",
        TacticalMap.WIN_TEXT, stkColor, stk);
      if (piece.subtitle) {
        ctx.font = `600 9px var(--font-mono, monospace)`;
        this.drawPieceText(ctx, piece.subtitle, textCenterX, y + ph * 0.78, "center", "middle",
          TacticalMap.WIN_SUB, stkColor, stk);
      }
    } else if (isLoss) {
      this.drawSkull(ctx, c, pw, ph, x, y);
      this.drawPieceText(ctx, mainText, textCenterX, y + ph * 0.42, "center", "middle",
        TacticalMap.LOSS_TEXT, stkColor, stk);
      if (piece.subtitle) {
        ctx.font = `600 9px var(--font-mono, monospace)`;
        this.drawPieceText(ctx, piece.subtitle, textCenterX, y + ph * 0.78, "center", "middle",
          TacticalMap.LOSS_SUB, stkColor, stk);
      }
    } else if (isCurrent) {
      // current：有文字无 icon
      this.drawPieceText(ctx, mainText, textCenterX, y + ph * 0.42, "center", "middle",
        TacticalMap.CUR_TEXT, stkColor, stk);
      if (piece.subtitle) {
        ctx.font = `600 9px var(--font-mono, monospace)`;
        this.drawPieceText(ctx, piece.subtitle, textCenterX, y + ph * 0.78, "center", "middle",
          TacticalMap.CUR_DOT, stkColor, stk);
      }
    } else {
      // V5 locked：暗底 + "?" 角标 + "未 战" 标签，与 terrain 区分
      // 中央 "?" — 暗白，中等透明度
      const qSize = Math.max(16, ph * 0.42);
      ctx.font = `bold ${qSize}px var(--font-mono, monospace)`;
      this.drawPieceText(ctx, "?", x + pw * 0.5, y + ph * 0.48, "center", "middle",
        TacticalMap.LOCKED_COLOR + "50", stkColor, stk);
      // 顶部 "未 战" 标签 — 小号 mono
      const labelSize = Math.max(8, ph * 0.15);
      ctx.font = `${labelSize}px var(--font-mono, monospace)`;
      this.drawPieceText(ctx, "未 战", x + pw * 0.5, y + ph * 0.24, "center", "middle",
        TacticalMap.LOCKED_COLOR + "40", stkColor, stk);
    }

    // ── V4.16 hover 覆盖层（牌面上方叠加，带动效）──
    const hAlpha = this.hoveredPieceId === piece.id ? this.hoverAlpha() : 0;
    const fAlpha = !this.hoveredPieceId && this.lastHoveredPieceId === piece.id ? this.hoverFadeOutAlpha() : 0;
    const alpha = Math.max(hAlpha, fAlpha);
    if (alpha > 0) {
      // 保存上下文用于透明度控制
      ctx.save();
      ctx.globalAlpha = alpha;
      // 半透明遮罩（暗底）
      ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
      ctx.beginPath();
      this.roundRect(ctx, x, y, pw, ph, cr);
      ctx.fill();

      // 按钮缩放动效：从 0.85 缓入到 1.0
      const scale = 0.85 + 0.15 * (hAlpha > 0 ? hAlpha : 1);
      const btnCx = x + pw / 2;
      const btnCy = y + ph / 2;

      if (isWin) {
        // 胜利 → 显示"胜利"文字
        const hfSize = Math.max(18, ph * 0.45);
        ctx.font = `bold ${hfSize}px var(--font-serif, serif)`;
        this.drawPieceText(ctx, "胜  利", btnCx, btnCy, "center", "middle",
          TacticalMap.WIN_TEXT, stkColor, stk);
      } else if (isLoss) {
        // 失败 → 显示"再次挑战"按钮（带动效缩放）
        const bw = pw * 0.58 * scale;
        const bh = ph * 0.48 * scale;
        this.drawHoverButton(ctx, btnCx, btnCy, bw, bh, "再次挑战", TacticalMap.LOSS_COLOR);
      } else if (piece.status === "locked") {
        // 未翻牌 → 显示"挑战"按钮（带动效缩放）
        const bw = pw * 0.45 * scale;
        const bh = ph * 0.48 * scale;
        this.drawHoverButton(ctx, btnCx, btnCy, bw, bh, "挑  战", TacticalMap.CUR_TEXT);
      }
      ctx.restore();
    }
  }

  // ── 斜线填充（V4.13.1 修正：spacing 为垂直距离，51° 时水平步进 = 4.5/sin(51°) ≈ 5.79）──
  private drawHatchFill(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number,
    color: string, isCurrent: boolean
  ): void {
    ctx.save();
    ctx.beginPath();
    this.roundRect(ctx, x, y, w, h, r);
    ctx.clip();

    const alpha = isCurrent ? TacticalMap.HATCH_ALPHA_CUR : TacticalMap.HATCH_ALPHA;
    ctx.strokeStyle = color + alpha;
    ctx.lineWidth = TacticalMap.HATCH_WIDTH;
    const angle = (TacticalMap.HATCH_ANGLE_DEG * Math.PI) / 180;
    const spacing = TacticalMap.HATCH_SPACING;        // 垂直距离（px）
    // 水平步进：spacing / sin(angle) —— 51° 时 ≈ 5.79
    const horizStep = spacing / Math.max(Math.abs(Math.sin(angle)), 0.01);
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    // 覆盖整个卡面（含超出）需要的水平范围
    const totalRange = w + h + spacing * 2;
    const count = Math.ceil(totalRange / horizStep) + 2;
    // 每条线的长度（足够跨越对角线）
    const lineLen = (w + h) * 1.5;
    const baseY = y + h;            // 从牌底外侧开始画
    const baseX = x - h;            // 从牌左外侧开始画

    for (let i = 0; i < count; i++) {
      const offset = i * horizStep;
      ctx.beginPath();
      ctx.moveTo(baseX + offset, baseY);
      ctx.lineTo(baseX + offset + lineLen * cosA, baseY - lineLen * sinA);
      ctx.stroke();
    }

    ctx.restore();
  }

  // ── 圆形棋子（V4.7 统一：边框改暗灰，移除脉动缩放）──
  private drawCirclePiece(ctx: CanvasRenderingContext2D, piece: BoardPiece): void {
    const c = this.pieceCoords.get(piece.id);
    if (!c) return;
    const cs = this.cellH;
    const r = cs * 0.36;
    const isCurrent = piece.status === "current";
    const color = this.statusColor(piece.status);

    if (isCurrent) {
      ctx.fillStyle = this.themeColor + "12";
      ctx.beginPath(); ctx.arc(c.x, c.y, r + 6, 0, Math.PI * 2); ctx.fill();
    }

    ctx.beginPath(); ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
    ctx.fillStyle = isCurrent ? this.themeColor + "1A" : "#0A1218";
    ctx.fill();
    ctx.strokeStyle = TacticalMap.UNIFIED_BORDER;
    ctx.lineWidth = isCurrent ? 1.5 : 1;
    ctx.stroke();

    ctx.beginPath(); ctx.arc(c.x, c.y, r * 0.55, 0, Math.PI * 2);
    ctx.strokeStyle = color + "40";
    ctx.lineWidth = 0.5;
    ctx.stroke();

    const fontSize = Math.max(10, cs * 0.18);
    ctx.font = `bold ${fontSize}px var(--font-serif, serif)`;
    ctx.fillStyle = isCurrent ? color : color + "C0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(piece.name.charAt(0), c.x, c.y);
  }

  // ── 大本营（V4.7 统一：边框改暗灰，移除脉动缩放）──
  private drawHQPiece(ctx: CanvasRenderingContext2D, piece: BoardPiece): void {
    const c = this.pieceCoords.get(piece.id);
    if (!c) return;
    const cs = this.cellH;
    const w = cs * 2.2;
    const h = cs * 1.2;
    const isCurrent = piece.status === "current";
    const color = isCurrent ? "#FF4655" : "#A33A48";

    const dx = c.x - w / 2;
    const dy = c.y - h / 2;

    if (isCurrent) {
      ctx.fillStyle = "rgba(255, 70, 85, 0.12)";
      ctx.fillRect(dx - 8, dy - 8, w + 16, h + 16);
    }

    ctx.fillStyle = isCurrent ? "rgba(255, 70, 85, 0.08)" : "#0A1218";
    ctx.fillRect(dx, dy, w, h);

    ctx.strokeStyle = TacticalMap.UNIFIED_BORDER;
    ctx.lineWidth = isCurrent ? 2 : 1.5;
    ctx.strokeRect(dx, dy, w, h);
    ctx.strokeStyle = color + "60";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(dx + 4, dy + 4, w - 8, h - 8);

    ctx.font = `bold ${Math.max(9, cs * 0.16)}px var(--font-serif, serif)`;
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("大  本  营", c.x, dy + 5);

    ctx.font = `bold ${Math.max(12, cs * 0.23)}px var(--font-serif, serif)`;
    ctx.fillText("将 帅 之 战", c.x, c.y + 4);

    ctx.font = `${Math.max(7, cs * 0.11)}px var(--font-mono, monospace)`;
    ctx.fillStyle = color + "90";
    ctx.textBaseline = "bottom";
    ctx.fillText("★★★★ · c010", c.x, dy + h - 3);
  }

  // ── terrain 图片预加载 ──
  private preloadTerrainImages(pieces: BoardPiece[]): void {
    for (const p of pieces) {
      if (p.type !== "terrain" || !p.terrainSvg) continue;
      if (this.terrainImages.has(p.terrainSvg)) continue;
      this.loadTerrainImage(p.terrainSvg);
    }
  }

  private async loadTerrainImage(relPath: string): Promise<HTMLImageElement | null> {
    if (this.terrainImages.has(relPath)) return this.terrainImages.get(relPath)!;
    try {
      const resp = await fetch(`/data/tiles/terrain/${relPath}`);
      if (!resp.ok) return null;
      let svgText = await resp.text();
      // CSS 变量替换为 terrain 玫红色系
      svgText = svgText
        .replace(/var\(--map-accent\b[^)]*\)/g, TacticalMap.TERRAIN_COLOR)
        .replace(/var\(--map-accent-alt\b[^)]*\)/g, TacticalMap.TERRAIN_ALT)
        .replace(/var\(--map-bg\b[^)]*\)/g, "#0D0A06")
        .replace(/var\(--map-grid\b[^)]*\)/g, TacticalMap.TERRAIN_COLOR + "4D")
        .replace(/var\(-[^)]+\)/g, TacticalMap.TERRAIN_ALT);
      const blob = new Blob([svgText], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error(`SVG load failed: ${relPath}`));
        el.src = url;
      });
      this.terrainImages.set(relPath, img);
      return img;
    } catch {
      return null;
    }
  }

  // ── 填充物地形牌面（1 格高 × 2 格宽，SVG 图标 + 地形名）──
  private drawTerrainPiece(ctx: CanvasRenderingContext2D, piece: BoardPiece): void {
    const c = this.pieceCoords.get(piece.id);
    if (!c) return;

    // V4.16 2×2 地形：高度加倍（2 行 × 2 列网格）
    const is2x2 = piece.terrainSize === "2x2";
    const pw = PIECE_W;
    const ph = is2x2 ? PIECE_H * 2 + GAP : PIECE_H;
    const cr = ph * 0.12;
    const tColor = TacticalMap.TERRAIN_COLOR;

    const x = c.x - pw / 2;
    const y = c.y - ph / 2;

    // 填充底
    ctx.fillStyle = TacticalMap.TERRAIN_BG;
    ctx.beginPath();
    this.roundRect(ctx, x, y, pw, ph, cr);
    ctx.fill();

    // 斜线填充（亮土黄色，2×2 加浓一点）
    this.drawHatchFill(ctx, x, y, pw, ph, cr, tColor, is2x2);

    // 统一外框（暗灰蓝）
    ctx.strokeStyle = TacticalMap.UNIFIED_BORDER;
    ctx.lineWidth = 1;
    ctx.beginPath();
    this.roundRect(ctx, x, y, pw, ph, cr);
    ctx.stroke();

    // 四角圆点（亮土黄，统一参数）
    ctx.fillStyle = tColor;
    for (const [dx, dy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
      ctx.beginPath();
      const dotX = dx > 0 ? x + TacticalMap.DOT_OFF + TacticalMap.DOT_R
                           : x + pw - TacticalMap.DOT_OFF - TacticalMap.DOT_R;
      const dotY = dy > 0 ? y + TacticalMap.DOT_OFF + TacticalMap.DOT_R
                           : y + ph - TacticalMap.DOT_OFF - TacticalMap.DOT_R;
      ctx.arc(dotX, dotY, TacticalMap.DOT_R, 0, Math.PI * 2);
      ctx.fill();
    }

    if (is2x2) {
      // ── 2×2 布局：图标居中放置，文本在下 ──
      const svgImg = piece.terrainSvg ? this.terrainImages.get(piece.terrainSvg) : undefined;
      if (svgImg && svgImg.complete && svgImg.naturalWidth > 0) {
        const iconH = ph * 0.38;           // 图标占上半部分
        const iconW = iconH;
        const iconX = x + (pw - iconW) / 2; // 水平居中
        const iconY = y + ph * 0.18;        // 偏上
        ctx.drawImage(svgImg, iconX, iconY, iconW, iconH);
      }
      // 地形名称（居中，在图标下方）
      const terrainName = piece.terrainName ?? piece.name;
      const fSize = Math.max(11, ph * 0.12);
      ctx.font = `bold ${fSize}px var(--font-serif, serif)`;
      this.drawPieceText(ctx, terrainName,
        x + pw * 0.50, y + ph * 0.72, "center", "middle",
        "#B0C0D0", TacticalMap.TEXT_STROKE_COLOR, TacticalMap.TEXT_STROKE);
    } else {
      // 1×2 布局：SVG 图标左对齐 + 文字居中（原有逻辑）
      const svgImg = piece.terrainSvg ? this.terrainImages.get(piece.terrainSvg) : undefined;
      if (svgImg && svgImg.complete && svgImg.naturalWidth > 0) {
        const iconH = ph * 0.675;
        const iconW = iconH;
        const iconX = x + pw * 0.06;
        const iconY = y + (ph - iconH) / 2;
        ctx.drawImage(svgImg, iconX, iconY, iconW, iconH);
      }
      const terrainName = piece.terrainName ?? piece.name;
      const fSize = Math.max(10, ph * 0.32);
      ctx.font = `bold ${fSize}px var(--font-serif, serif)`;
      this.drawPieceText(ctx, terrainName,
        x + pw * 0.50, y + ph * 0.50, "center", "middle",
        "#B0C0D0", TacticalMap.TEXT_STROKE_COLOR, TacticalMap.TEXT_STROKE);
    }

    // ── V4.16 hover 覆盖层（地形提示，带动效）──
    const hAlpha = this.hoveredPieceId === piece.id ? this.hoverAlpha() : 0;
    const fAlpha = !this.hoveredPieceId && this.lastHoveredPieceId === piece.id ? this.hoverFadeOutAlpha() : 0;
    const alpha = Math.max(hAlpha, fAlpha);
    if (alpha > 0) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "rgba(0, 0, 0, 0.30)";
      ctx.beginPath();
      this.roundRect(ctx, x, y, pw, ph, cr);
      ctx.fill();

      const name = piece.terrainName ?? piece.name;
      const hfSize = Math.max(13, ph * 0.18);
      ctx.font = `bold ${hfSize}px var(--font-serif, serif)`;
      const stk = TacticalMap.TEXT_STROKE;
      const stkColor = TacticalMap.TEXT_STROKE_COLOR;
      this.drawPieceText(ctx, name, x + pw / 2, y + ph / 2, "center", "middle",
        TacticalMap.TERRAIN_COLOR, stkColor, stk);
      ctx.restore();
    }
  }

  // ── 画圆角矩形路径 ──
  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number
  ): void {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  // ── 扫描线 ──
  private drawScanlines(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "rgba(0, 228, 255, 0.013)";
    for (let y = 0; y < this.totalH; y += 3) {
      ctx.fillRect(0, y, this.totalW, 1);
    }
  }

  // ── 坐标标注（纵轴行号）──
  private drawCoordLabels(ctx: CanvasRenderingContext2D): void {
    ctx.font = `${Math.max(7, this.cellH * 0.10)}px var(--font-mono, monospace)`;
    ctx.fillStyle = "rgba(0, 228, 255, 0.18)";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let r = 0; r < ROWS; r += 3) {
      const y = this.gridY(r);
      ctx.fillText(`${r}`, PADDING - 8, y);
    }

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let c = 0; c < COLS; c += 3) {
      const x = this.gridX(c);
      ctx.fillText(`${c}`, x, this.totalH - PADDING + 4);
    }
  }

  // ── 状态色 ──
  private statusColor(status: BoardPiece["status"]): string {
    switch (status) {
      case "win":     return "#7FD962";         // 绿 — 红星在牌面叠加
      case "current": return this.themeColor;    // 青 — 脉动
      case "loss":    return TacticalMap.LOSS_COLOR;   // 琥珀
      case "locked":  return TacticalMap.LOCKED_COLOR; // 暗白
    }
  }

  // ── 更新 ──
  update(pieces: BoardPiece[], _links: BoardLink[], themeColor: string, _cleared: string[]): void {
    this.pieces = pieces;
    this.themeColor = themeColor;
    this.preloadTerrainImages(pieces);
    this.resize();
  }

  // ── 销毁 ──
  destroy(): void {
    if (this.animId) cancelAnimationFrame(this.animId);
    this.resizeObserver?.disconnect();
    // V4.16 清理鼠标事件
    if (this.boundMouseMove) {
      this.canvas.removeEventListener("mousemove", this.boundMouseMove);
    }
    if (this.boundClick) {
      this.canvas.removeEventListener("click", this.boundClick);
    }
    this.onPieceClick = undefined;
  }
}
